import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ConfirmationDialog from '../../../components/ui/ConfirmationDialog';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  CollapseAllIcon,
  ExpandAllIcon,
  FolderIcon,
  PencilIcon,
  PlusIcon,
  SearchIcon,
  TrashIcon,
} from '../../../components/ui';
import { useAuth } from '../../../contexts/AuthContext';
import { useNotification } from '../../../contexts/NotificationContext';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import {
  getCoaTreeCollapseStorageKey,
  loadPersistedTreeCollapsedIds,
  persistTreeCollapsedIds,
} from '../../../lib/listStatePersistence';
import { useAccAccountTree, type AccAccountTreeNode } from './useData';
import { moduleConfig } from './config';
import { accountingReturnState } from '../shared/navigation';
import {
  buildHeaderTree,
  findAccountNodeById,
  flattenAccountTree,
  collectExpandableNodeIds,
  collectCollapsedIdsBelowRoot,
  filterAccountTree,
  getAccountPath,
  accountCanDelete,
  type TreeListRow,
} from './treeListUtils';
import { CoaChildrenInbox, type CoaCreateAction } from './CoaChildrenInbox';
import { CoaNodeFormPanel, type CoaFormMode } from './CoaNodeFormPanel';

const PANEL_WIDTH_KEY = `coa-groups-panel-width:${moduleConfig.module.route.list}`;
const INCLUDE_SUBGROUPS_KEY = `coa-include-subgroups:${moduleConfig.module.route.list}`;
const PANEL_MIN = 260;
const PANEL_MAX = 560;
const PANEL_DEFAULT = 384;

const SELECTED_GROUP_KEY = `coa-selected-group:${moduleConfig.module.route.list}`;

let cachedCollapsedIds: Set<number> | null = null;
let cachedSelectedGroupId: number | null | undefined;

const loadCollapsedIds = (): Set<number> => {
  const storageKey = getCoaTreeCollapseStorageKey(moduleConfig.module.route.list);
  const fromSession = loadPersistedTreeCollapsedIds(storageKey);
  if (fromSession) return fromSession;
  if (cachedCollapsedIds) return new Set(cachedCollapsedIds);
  return new Set();
};

const resolveInitialCollapsedIds = (headerTree: AccAccountTreeNode[]): Set<number> => {
  const storageKey = getCoaTreeCollapseStorageKey(moduleConfig.module.route.list);
  const fromSession = loadPersistedTreeCollapsedIds(storageKey);
  if (fromSession) return fromSession;
  if (cachedCollapsedIds) return new Set(cachedCollapsedIds);
  return new Set(collectCollapsedIdsBelowRoot(headerTree));
};

const loadSelectedGroupId = (): number | null => {
  if (cachedSelectedGroupId !== undefined) return cachedSelectedGroupId;
  try {
    const raw = sessionStorage.getItem(SELECTED_GROUP_KEY);
    if (raw == null || raw === '') return null;
    const id = Number(raw);
    return Number.isFinite(id) ? id : null;
  } catch {
    return null;
  }
};

const loadPanelWidth = (): number => {
  try {
    const raw = localStorage.getItem(PANEL_WIDTH_KEY);
    const n = raw != null ? Number(raw) : PANEL_DEFAULT;
    if (!Number.isFinite(n)) return PANEL_DEFAULT;
    return Math.min(PANEL_MAX, Math.max(PANEL_MIN, n));
  } catch {
    return PANEL_DEFAULT;
  }
};

const loadIncludeSubgroups = (): boolean => {
  try {
    const raw = localStorage.getItem(INCLUDE_SUBGROUPS_KEY);
    if (raw === null) return false;
    return raw === '1';
  } catch {
    return false;
  }
};

const List: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  const { showSuccess, showError } = useNotification();
  const { can } = useAuth();
  const returnState = accountingReturnState(location.pathname, location.search);

  const canDelete = can(moduleConfig.module.permissionModuleKey ?? 'acc_accounts', 'delete');
  const canEdit = can(moduleConfig.module.permissionModuleKey ?? 'acc_accounts', 'edit');

  const { data: tree, isLoading, mutate } = useAccAccountTree();

  const [groupFilter, setGroupFilter] = useState('');
  const [includeSubgroups, setIncludeSubgroups] = useState(loadIncludeSubgroups);
  const [panelWidth, setPanelWidth] = useState(loadPanelWidth);
  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(loadSelectedGroupId);
  const [collapsedIds, setCollapsedIds] = useState<Set<number>>(loadCollapsedIds);
  const [deleteConfirm, setDeleteConfirm] = useState<{ id: number; kind: CoaFormMode } | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<Date | null>(null);
  const [treeCollapseReady, setTreeCollapseReady] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<CoaFormMode>('account');
  const [formParentId, setFormParentId] = useState<number | null>(null);
  const [formEditItem, setFormEditItem] = useState<AccAccountTreeNode | null>(null);

  const resizingRef = useRef(false);
  const panelWidthRef = useRef(panelWidth);
  const splitRef = useRef<HTMLDivElement>(null);
  const [panelResizing, setPanelResizing] = useState(false);

  const headerTree = useMemo(() => buildHeaderTree(tree), [tree]);
  const filteredHeaderTree = useMemo(
    () => filterAccountTree(headerTree, groupFilter),
    [headerTree, groupFilter],
  );
  const isGroupFilterActive = groupFilter.trim().length > 0;

  useEffect(() => {
    if (isLoading || headerTree.length === 0 || treeCollapseReady) return;
    setCollapsedIds(resolveInitialCollapsedIds(headerTree));
    setTreeCollapseReady(true);
  }, [isLoading, headerTree, treeCollapseReady]);

  useEffect(() => {
    const storageKey = getCoaTreeCollapseStorageKey(moduleConfig.module.route.list);
    persistTreeCollapsedIds(storageKey, collapsedIds);
    cachedCollapsedIds = new Set(collapsedIds);
  }, [collapsedIds]);

  useEffect(() => {
    cachedSelectedGroupId = selectedGroupId;
    try {
      if (selectedGroupId == null) sessionStorage.removeItem(SELECTED_GROUP_KEY);
      else sessionStorage.setItem(SELECTED_GROUP_KEY, String(selectedGroupId));
    } catch {
      /* ignore */
    }
  }, [selectedGroupId]);

  useEffect(() => {
    try {
      localStorage.setItem(INCLUDE_SUBGROUPS_KEY, includeSubgroups ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, [includeSubgroups]);

  useEffect(() => {
    panelWidthRef.current = panelWidth;
  }, [panelWidth]);

  useEffect(() => {
    if (headerTree.length === 0) {
      if (selectedGroupId != null) setSelectedGroupId(null);
      return;
    }
    if (selectedGroupId != null && findAccountNodeById(headerTree, selectedGroupId)) return;
    setSelectedGroupId(headerTree[0]?.id ?? null);
  }, [headerTree, selectedGroupId]);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!resizingRef.current || !splitRef.current) return;
      const rect = splitRef.current.getBoundingClientRect();
      const next = Math.min(PANEL_MAX, Math.max(PANEL_MIN, e.clientX - rect.left));
      setPanelWidth(next);
    };
    const onUp = () => {
      if (!resizingRef.current) return;
      resizingRef.current = false;
      setPanelResizing(false);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      try {
        localStorage.setItem(PANEL_WIDTH_KEY, String(panelWidthRef.current));
      } catch {
        /* ignore */
      }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);

  const expandableIds = useMemo(() => collectExpandableNodeIds(headerTree), [headerTree]);

  const groupRows = useMemo(() => {
    const collapsed = isGroupFilterActive ? new Set<number>() : collapsedIds;
    return flattenAccountTree(filteredHeaderTree, collapsed);
  }, [filteredHeaderTree, collapsedIds, isGroupFilterActive]);

  const groupTreeGutterPx = useMemo(() => {
    const maxDepth = groupRows.reduce((max, row) => Math.max(max, row.depth), 0);
    return maxDepth * 14 + 22;
  }, [groupRows]);

  const selectedGroup = useMemo(
    () => (selectedGroupId != null ? findAccountNodeById(tree, selectedGroupId) : null),
    [tree, selectedGroupId],
  );

  const breadcrumb = useMemo(
    () => (selectedGroupId != null ? getAccountPath(headerTree, selectedGroupId) : []),
    [headerTree, selectedGroupId],
  );

  const shortTitle = selectedGroup?.name ?? 'Select a group';
  const pathTitle = useMemo(() => {
    if (breadcrumb.length === 0) return '';
    return breadcrumb.map((n) => n.name).join(' · ');
  }, [breadcrumb]);

  const toggleCollapsed = useCallback((id: number) => {
    setCollapsedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const expandAll = useCallback(() => setCollapsedIds(new Set()), []);
  const collapseAll = useCallback(() => setCollapsedIds(new Set(expandableIds)), [expandableIds]);

  const selectGroup = useCallback((id: number) => {
    setSelectedGroupId(id);
    setFormOpen(false);
  }, []);

  const startResize = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return;
    e.preventDefault();
    resizingRef.current = true;
    setPanelResizing(true);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);

  const openCreateForm = useCallback((mode: CoaFormMode, parentId: number | null) => {
    if (parentId != null) setSelectedGroupId(parentId);
    setFormMode(mode);
    setFormParentId(parentId);
    setFormEditItem(null);
    setFormOpen(true);
  }, []);

  const openEditForm = useCallback((mode: CoaFormMode, item: AccAccountTreeNode) => {
    if (!item.isPosting) setSelectedGroupId(item.id);
    else if (item.parentId != null) setSelectedGroupId(item.parentId);
    setFormMode(mode);
    setFormParentId(item.parentId ?? null);
    setFormEditItem(item);
    setFormOpen(true);
  }, []);

  const createActions = useMemo((): CoaCreateAction[] => {
    if (!canEdit || !selectedGroup || selectedGroupId == null) return [];
    return [
      {
        id: 'account',
        label: 'Account',
        hint: 'Create a ledger account under this group',
        primary: true,
        onClick: () => openCreateForm('account', selectedGroupId),
      },
      {
        id: 'subgroup',
        label: 'Subgroup',
        hint: 'Create a header group under this group',
        onClick: () => openCreateForm('group', selectedGroupId),
      },
    ];
  }, [canEdit, selectedGroup, selectedGroupId, openCreateForm]);

  const closeForm = useCallback(() => setFormOpen(false), []);

  const handleDelete = async (id: number) => {
    try {
      await moduleConfig.delete.handler(id);
      await mutate();
      if (selectedGroupId === id) setSelectedGroupId(null);
      setDeleteConfirm(null);
      setFormOpen(false);
      showSuccess(deleteConfirm?.kind === 'group' ? 'Group deleted' : 'Account deleted');
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } }; message?: string };
      showError(err?.response?.data?.message || err?.message || moduleConfig.delete.errorMessage);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await mutate();
      setLastRefreshedAt(new Date());
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <div className="flex flex-col h-full min-h-0 p-1 gap-1">
      <div
        ref={splitRef}
        className={cn(
          'flex flex-1 min-h-0 overflow-hidden rounded-xl border shadow-sm',
          colors.surface,
          colors.border,
        )}
      >
        {/* Left: group tree */}
        <aside
          className={cn('shrink-0 flex flex-col min-h-0', colors.border)}
          style={{ width: panelWidth }}
        >
          <div className={cn('px-3 py-2.5 border-b flex items-center justify-between gap-2', colors.border)}>
            <p className={cn('text-xs font-semibold uppercase tracking-wider', colors.textMuted)}>Groups</p>
            <div className="flex items-center gap-0.5 shrink-0">
              {canEdit && (
                <button
                  type="button"
                  onClick={() => openCreateForm('group', null)}
                  className={cn(
                    'inline-flex items-center justify-center h-7 w-7 rounded-md transition-colors',
                    colors.primaryFg,
                    colors.interactiveHover,
                  )}
                  title="New root group"
                  aria-label="New root group"
                >
                  <PlusIcon className="h-3.5 w-3.5" />
                </button>
              )}
              <button
                type="button"
                onClick={expandAll}
                disabled={isGroupFilterActive || expandableIds.length === 0}
                className={cn(
                  'inline-flex items-center justify-center h-7 w-7 rounded-md transition-colors',
                  colors.textMuted,
                  colors.interactiveHover,
                  'disabled:opacity-40 disabled:pointer-events-none',
                )}
                title="Expand all"
                aria-label="Expand all"
              >
                <ExpandAllIcon className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={collapseAll}
                disabled={isGroupFilterActive || expandableIds.length === 0}
                className={cn(
                  'inline-flex items-center justify-center h-7 w-7 rounded-md transition-colors',
                  colors.textMuted,
                  colors.interactiveHover,
                  'disabled:opacity-40 disabled:pointer-events-none',
                )}
                title="Collapse all"
                aria-label="Collapse all"
              >
                <CollapseAllIcon className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>

          <div className={cn('px-3 py-2 border-b', colors.border)}>
            <div className="relative">
              <SearchIcon
                className={cn('absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 pointer-events-none', colors.textMuted)}
              />
              <input
                type="search"
                value={groupFilter}
                onChange={(e) => setGroupFilter(e.target.value)}
                placeholder="Filter..."
                className={cn(
                  'w-full h-8 pl-8 pr-3 rounded-md border text-sm',
                  colors.border,
                  colors.surface,
                  colors.text,
                  'focus:outline-none focus:ring-2 focus:ring-indigo-500/40',
                )}
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto dashboard-scrollbar py-1">
            {isLoading && (
              <p className={cn('px-3 py-4 text-sm', colors.textMuted)}>Loading groups...</p>
            )}

            {!isLoading && headerTree.length === 0 && (
              <p className={cn('px-3 py-6 text-sm text-center', colors.textMuted)}>
                No header groups yet. Create a non-posting account to start the hierarchy.
              </p>
            )}

            {!isLoading && headerTree.length > 0 && groupRows.length === 0 && (
              <p className={cn('px-3 py-6 text-sm text-center', colors.textMuted)}>No groups match.</p>
            )}

            {!isLoading &&
              groupRows.map((row: TreeListRow) => {
                const active = selectedGroupId === row.id && !formOpen;
                const isExpanded = row.hasChildren && !collapsedIds.has(row.id);
                const code = row.combLevel ?? row.code;
                const deletable = accountCanDelete(row);
                return (
                  <div
                    key={row.id}
                    role="treeitem"
                    aria-selected={selectedGroupId === row.id}
                    aria-expanded={row.hasChildren ? isExpanded : undefined}
                    onClick={() => selectGroup(row.id)}
                    className={cn(
                      'group/row flex items-center gap-1.5 h-7 cursor-pointer select-none transition-colors pl-2 pr-1.5',
                      colors.text,
                      active
                        ? cn(colors.info, 'ring-1 ring-inset', colors.primaryBorderAll)
                        : colors.interactiveHover,
                    )}
                    title={`${row.name}`}
                  >
                    <span
                      className="shrink-0 flex items-center"
                      style={{ width: groupTreeGutterPx, paddingLeft: row.depth * 14 }}
                    >
                      {row.hasChildren ? (
                        <button
                          type="button"
                          className={cn(
                            'h-5 w-5 inline-flex items-center justify-center rounded -m-0.5',
                            colors.textMuted,
                            colors.interactiveHover,
                          )}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleCollapsed(row.id);
                          }}
                          title={isExpanded ? 'Collapse' : 'Expand'}
                          aria-label={isExpanded ? 'Collapse' : 'Expand'}
                        >
                          {isExpanded ? (
                            <ChevronDownIcon className="h-3.5 w-3.5" />
                          ) : (
                            <ChevronRightIcon className="h-3.5 w-3.5" />
                          )}
                        </button>
                      ) : (
                        <span className="w-5" aria-hidden />
                      )}
                    </span>

                    <span className="shrink-0 w-[7.25rem] flex items-center gap-1.5 min-w-0 text-xs">
                      <span
                        className={cn('shrink-0', isDark ? 'text-violet-400' : 'text-violet-500')}
                        title="Header"
                      >
                        <FolderIcon className="h-3.5 w-3.5" aria-hidden />
                      </span>
                      <span className={cn('tabular-nums truncate', colors.text)} title={code}>
                        {code}
                      </span>
                    </span>

                    <span
                      className={cn(
                        'min-w-0 flex-1 truncate text-xs',
                        active ? 'font-medium' : 'font-normal',
                      )}
                      title={row.name}
                    >
                      {row.name}
                    </span>

                    {(canEdit || canDelete) && (
                      <span
                        className={cn(
                          'shrink-0 flex items-center gap-0.5 opacity-0 group-hover/row:opacity-100 focus-within:opacity-100',
                          selectedGroupId === row.id && 'opacity-100',
                        )}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {canEdit && (
                          <>
                            <button
                              type="button"
                              className={cn('p-0.5 rounded', colors.primaryFg, colors.interactiveHover)}
                              title="Edit group"
                              aria-label="Edit group"
                              onClick={() => openEditForm('group', row)}
                            >
                              <PencilIcon className="h-3 w-3" />
                            </button>
                            <button
                              type="button"
                              className={cn('p-0.5 rounded', colors.primaryFg, colors.interactiveHover)}
                              title="Add subgroup"
                              aria-label="Add subgroup"
                              onClick={() => openCreateForm('group', row.id)}
                            >
                              <PlusIcon className="h-3 w-3" />
                            </button>
                          </>
                        )}
                        {canDelete && (
                          <button
                            type="button"
                            disabled={!deletable}
                            className={cn(
                              'p-0.5 rounded',
                              deletable
                                ? cn(isDark ? 'text-red-400' : 'text-red-600', colors.interactiveHover)
                                : cn(colors.textMuted, 'cursor-not-allowed'),
                            )}
                            title={deletable ? 'Delete group' : 'Cannot delete — has children or journals'}
                            aria-label="Delete group"
                            onClick={() => setDeleteConfirm({ id: row.id, kind: 'group' })}
                          >
                            <TrashIcon className="h-3 w-3" />
                          </button>
                        )}
                      </span>
                    )}
                  </div>
                );
              })}
          </div>
        </aside>

        {/* Resize handle */}
        <div
          role="separator"
          aria-orientation="vertical"
          aria-valuenow={panelWidth}
          aria-label="Resize groups panel"
          title="Drag to resize"
          onMouseDown={startResize}
          className={cn(
            'flex shrink-0 w-1.5 cursor-col-resize items-center justify-center group',
            colors.interactiveHover,
            'border-l',
            colors.border,
            panelResizing && colors.surfaceHover,
          )}
        >
          <div
            className={cn(
              'w-0.5 h-12 rounded-full opacity-60 group-hover:opacity-100',
              isDark ? 'bg-slate-500' : 'bg-slate-400',
            )}
          />
        </div>

        {/* Right: form panel or accounts inbox */}
        <section className="flex min-h-0 min-w-0 flex-1 flex-col p-2 sm:p-3">
          {formOpen ? (
            <CoaNodeFormPanel
              mode={formMode}
              parentId={formParentId}
              editItem={formEditItem}
              onCancel={closeForm}
              onSaved={() => {
                void mutate();
                closeForm();
              }}
            />
          ) : !selectedGroup ? (
            <div
              className={cn(
                'flex flex-1 items-center justify-center rounded-2xl border border-dashed p-6',
                colors.border,
                colors.surface,
              )}
            >
              <div className="w-full max-w-sm px-6 py-12 text-center">
                <span
                  className={cn(
                    'mx-auto mb-3 inline-flex h-11 w-11 items-center justify-center rounded-xl',
                    colors.headerBg,
                    colors.textMuted,
                  )}
                >
                  <FolderIcon className="h-5 w-5" />
                </span>
                <p className={cn('text-sm font-medium', colors.text)}>Select a group</p>
                <p className={cn('mt-1 text-xs', colors.textMuted)}>
                  Pick a header group on the left to view accounts and add records.
                </p>
              </div>
            </div>
          ) : (
            <CoaChildrenInbox
              title={shortTitle}
              description={pathTitle !== shortTitle ? pathTitle : undefined}
              selectedGroup={selectedGroup}
              canEdit={canEdit}
              canDelete={canDelete}
              isLoading={isLoading}
              createActions={createActions}
              onEditSelected={canEdit ? () => openEditForm('group', selectedGroup) : null}
              onViewAccount={(row) =>
                navigate(moduleConfig.module.route.edit(row.id), { state: returnState })
              }
              onEditAccount={(row) =>
                navigate(moduleConfig.module.route.edit(row.id), { state: returnState })
              }
              onDeleteAccount={(row) => setDeleteConfirm({ id: row.id, kind: 'account' })}
              onSelectSubgroup={selectGroup}
              isRefreshing={isRefreshing}
              lastRefreshedAt={lastRefreshedAt}
              onRefresh={() => {
                void handleRefresh();
              }}
              includeSubgroups={includeSubgroups}
              onIncludeSubgroupsChange={setIncludeSubgroups}
            />
          )}
        </section>
      </div>

      {deleteConfirm != null && (
        <ConfirmationDialog
          isOpen
          onClose={() => setDeleteConfirm(null)}
          onConfirm={() => handleDelete(deleteConfirm.id)}
          title={deleteConfirm.kind === 'group' ? 'Delete group' : moduleConfig.delete.title}
          message={
            deleteConfirm.kind === 'group'
              ? 'Delete this group? It must have no subgroups or ledger accounts.'
              : moduleConfig.delete.message
          }
          confirmText={moduleConfig.delete.confirmText}
          cancelText={moduleConfig.delete.cancelText}
          confirmVariant="danger"
        />
      )}
    </div>
  );
};

export default List;
